// src/utils/toPdf.js
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import Cookies from 'js-cookie';
import { UrlDev } from '../url';
import { USE_PDF_SERVER } from '../constants';

// Body has a DPI-compensation scale transform applied in App.js. html2canvas
// captures the rendered DOM and gets confused by ancestor transforms (text
// metrics and positions come out wrong). Temporarily strip the transform
// during capture and restore it after.
const withBodyTransformDisabled = async (fn) => {
  const body = document.body;
  const html = document.documentElement;
  const saved = {
    transform: body.style.transform,
    transformOrigin: body.style.transformOrigin,
    width: body.style.width,
    height: body.style.height,
    uiScale: html.style.getPropertyValue('--ui-scale'),
  };
  // Reset body transform so html2canvas sees the page at natural scale
  body.style.transform = 'none';
  body.style.width = '';
  body.style.height = '';
  // Reset --ui-scale to 1 so CSS rules that compensate for the transform
  // (modal-overlay, view-details, navigation/app-container heights) don't
  // render at over-scaled sizes during capture.
  html.style.setProperty('--ui-scale', '1');
  try {
    return await fn();
  } finally {
    body.style.transform = saved.transform;
    body.style.transformOrigin = saved.transformOrigin;
    body.style.width = saved.width;
    body.style.height = saved.height;
    if (saved.uiScale) {
      html.style.setProperty('--ui-scale', saved.uiScale);
    } else {
      html.style.removeProperty('--ui-scale');
    }
  }
};


export const toPdf_pages = async ({
  container = ".pdf-capture",
  filename = "document",
  returnBlob = false,
  scale = 2,
  imageType = "JPEG", // or "PNG"
  imageQuality = 0.85 // Slightly higher for clearer text
}) => {
  // ✅ Wait for web fonts to fully load before capturing
  if (document.fonts && document.fonts.ready) {
    try {
      await document.fonts.ready;
    } catch (err) {
      console.warn("Fonts might not have fully loaded:", err);
    }
  }

  const elements = document.querySelectorAll(container);

  if (!elements.length) {
    alert("No elements found to export!");
    return;
  }

  // ✅ Validate scale range
  if (typeof scale !== "number" || scale < 1 || scale > 5) {
    scale = 2;
  }

  // ✅ Create PDF instance
  const pdf = new jsPDF("p", "mm", "a4");
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();

  for (let i = 0; i < elements.length; i++) {
    const element = elements[i];

    // ✅ Wait for images inside the container to load before capturing
    await Promise.all(
      Array.from(element.querySelectorAll("img"))
        .filter(img => !img.complete)
        .map(img => new Promise(res => (img.onload = img.onerror = res)))
    );

    // ✅ Capture high-quality canvas (with body transform disabled - see helper)
    const canvas = await withBodyTransformDisabled(() => html2canvas(element, {
      scale,
      useCORS: true,
      logging: false,
      width: element.scrollWidth,
      height: element.scrollHeight,
      backgroundColor: "#ffffff"
    }));

    const imgData = canvas.toDataURL(
      imageType === "PNG" ? "image/png" : "image/jpeg",
      imageType === "PNG" ? undefined : imageQuality
    );

    const imgWidth = canvas.width;
    const imgHeight = canvas.height;

    const scaleFactor = pageWidth / imgWidth;
    const finalWidth = pageWidth;
    const finalHeight = imgHeight * scaleFactor;

    // ✅ Add each element as a new page
    if (i > 0) pdf.addPage();

    if (finalHeight > pageHeight) {
      // If content is taller than A4, scale to fit height instead
      const heightScaleFactor = pageHeight / imgHeight;
      const adjustedWidth = imgWidth * heightScaleFactor;
      const adjustedHeight = pageHeight;

      pdf.addImage(
        imgData,
        imageType,
        (pageWidth - adjustedWidth) / 2,
        0,
        adjustedWidth,
        adjustedHeight
      );
    } else {
      pdf.addImage(imgData, imageType, 0, 0, finalWidth, finalHeight);
    }
  }

  // ✅ Save or return blob
  if (returnBlob) {
    return pdf.output("blob");
  } else {
    const today = new Date().toISOString().split("T")[0];
    pdf.save(`${filename}-${today}.pdf`);
  }
};

export const toPdf = async (options = {}) => {
  const {
    container = ".view-details",
    filename = "document",
    returnBlob = false,
    scale = 1.2,                     // 🔧 Lower scale = smaller file
    imageType = "jpeg",              // 🔄 Use JPEG instead of PNG
    imageQuality = 0.85              // 🧪 JPEG compression (0-1)
  } = options;

  const element = document.querySelector(container);
  if (!element) {
    alert("Element not found!");
    return;
  }

  // Temporarily adjust layout for full capture
  const originalHeight = element.style.height;
  const originalOverflow = element.style.overflow;
  element.style.height = element.scrollHeight + "px";
  element.style.overflow = "visible";

  const canvas = await withBodyTransformDisabled(() => html2canvas(element, {
    scale,
    useCORS: true,
    scrollY: -window.scrollY
  }));

  // Restore original styles
  element.style.height = originalHeight;
  element.style.overflow = originalOverflow;

  const pdf = new jsPDF("p", "mm", "a4");
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();

  const imgWidth = pageWidth;
  const imgHeight = (canvas.height * imgWidth) / canvas.width;

  let position = 0;
  const pxPerMm = canvas.width / pageWidth;
  const pageHeightPx = pageHeight * pxPerMm;

  while (position < canvas.height) {
    const pageCanvas = document.createElement("canvas");
    const context = pageCanvas.getContext("2d");

    pageCanvas.width = canvas.width;
    pageCanvas.height = Math.min(pageHeightPx, canvas.height - position);

    context.drawImage(
      canvas,
      0,
      position,
      canvas.width,
      pageCanvas.height,
      0,
      0,
      canvas.width,
      pageCanvas.height
    );

    const pageImgData = pageCanvas.toDataURL(`image/${imageType}`, imageQuality);
    if (position > 0) pdf.addPage();

    pdf.addImage(
      pageImgData,
      imageType.toUpperCase(),
      0,
      0,
      imgWidth,
      (pageCanvas.height * imgWidth) / canvas.width
    );

    position += pageHeightPx;
  }

  if (returnBlob) {
    return pdf.output("blob");
  } else {
    const today = new Date().toISOString().split("T")[0];
    pdf.save(`${filename}-${today}.pdf`);
  }
};


// ----------------------------------------------------------------------------
// Server-side PDF generation (Playwright/headless Chromium on doorapi).
//
// Pros over the legacy html2canvas+jsPDF pipeline:
//   - Vector text (sharp, selectable, searchable, small files)
//   - No DPI/scaling issues (server always renders at 1.0)
//   - No PDF concerns from ancestor CSS transforms
//
// How it works:
//   1. Capture the target element's outerHTML.
//   2. Collect all readable CSS rules from document.styleSheets and inline
//      them into a single <style> tag so the backend's headless browser does
//      not need to fetch any external resources.
//   3. POST the resulting self-contained HTML to /pdf/render.
//   4. Receive the PDF as a binary blob and return it (or trigger download).
// ----------------------------------------------------------------------------

const collectAllStyles = () => {
  // Pull every accessible CSSRule from the document's stylesheets. We swallow
  // SecurityErrors for cross-origin sheets (they're typically not used here).
  const chunks = [];
  for (const sheet of Array.from(document.styleSheets)) {
    try {
      for (const rule of Array.from(sheet.cssRules)) {
        chunks.push(rule.cssText);
      }
    } catch (e) {
      // Cross-origin stylesheet (no CORS) - skip silently.
    }
  }
  return chunks.join('\n');
};

const buildStandaloneHtml = (innerHtml, extraCss = '', bodyClass = '') => {
  const css = collectAllStyles();
  // <base href=...> lets headless Chromium resolve relative URLs (logos,
  // fonts, etc.) back to the live frontend so images load instead of breaking.
  const baseHref = window.location.origin + '/';
  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <base href="${baseHref}">
  <style>
    /* Reset body transform/width applied by App.js for DPI compensation -
       the backend renders at 1.0, so we don't want these scaling tricks. */
    body { transform: none !important; width: auto !important; height: auto !important; }
    ${css}
    ${extraCss}
  </style>
</head>
<body class="${bodyClass}">
${innerHtml}
</body>
</html>`;
};

export const toPdfBackend = async ({
  container = '.view-details',
  filename = 'document',
  returnBlob = false,
  format = 'A4',
  landscape = false,
  margin = '10mm',
  extraCss = '',
  // CSS class applied to <body> in the standalone HTML the backend renders.
  // Use this to scope iterative test overrides (e.g. 'pdf-test-mode' which
  // activates rules from pdf-test.css).
  bodyClass = '',
  // CSS pixel width of the layout viewport used by headless Chromium. Pick
  // a value close to your live browser width so fixed-pixel content (e.g.
  // inline-width SVGs) renders at the same relative size as in the live UI -
  // the backend will then scale it down to fit the PDF format.
  viewportWidth = 1500,
} = {}) => {
  const elements = Array.from(document.querySelectorAll(container));
  if (!elements.length) {
    alert('Element not found: ' + container);
    return null;
  }

  // For each captured element, temporarily expand height so overflow content
  // is included, snapshot outerHTML, then restore styles.
  const savedStyles = elements.map(el => ({
    height: el.style.height,
    overflow: el.style.overflow,
  }));
  elements.forEach(el => {
    el.style.height = el.scrollHeight + 'px';
    el.style.overflow = 'visible';
  });
  const elementHtmls = elements.map(el => el.outerHTML);
  elements.forEach((el, i) => {
    el.style.height = savedStyles[i].height;
    el.style.overflow = savedStyles[i].overflow;
  });

  // Each .pdf-capture in the React tree is designed as one logical PDF page
  // (the component already controls what content appears on which page).
  // Wrap each in a div with break-inside: avoid + break-after: page so the
  // browser keeps the capture intact and starts a new page after.
  const innerHtml = elementHtmls
    .map((h, i) => {
      const isLast = i === elementHtmls.length - 1;
      const breakAfter = isLast ? '' : 'page-break-after: always; break-after: page;';
      return `<div style="page-break-inside: avoid; break-inside: avoid; ${breakAfter}">${h}</div>`;
    })
    .join('');

  const html = buildStandaloneHtml(innerHtml, extraCss, bodyClass);

  const token = Cookies.get('access_token');
  const response = await fetch(`${UrlDev}/pdf/render`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ html, format, landscape, margin, viewport_width: viewportWidth }),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`PDF render failed (${response.status}): ${text}`);
  }

  const blob = await response.blob();

  if (returnBlob) {
    return blob;
  }

  // Trigger download
  const today = new Date().toISOString().split('T')[0];
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}-${today}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
  return blob;
};

// ----------------------------------------------------------------------------
// Pipeline-agnostic facade. Each per-product PDF button (window editor,
// patio doors, pleated screens, etc.) calls this single function and the
// USE_PDF_SERVER flag from constants.js decides which engine renders the
// PDF. Lets us flip the entire app between server-side (vector, small file)
// and legacy html2canvas (raster, larger file) with one constant.
//
// Accepts the union of options - server-only options are ignored by the
// legacy path and vice versa.
// ----------------------------------------------------------------------------
export const toPdfAuto = async (options = {}) => {
  if (USE_PDF_SERVER) {
    return toPdfBackend({
      // Reasonable defaults matching the Test pipeline we tuned.
      format: 'A4',
      margin: '0',
      bodyClass: 'pdf-test-mode',
      ...options,
    });
  }
  return toPdf(options);
};

